'use client';

import { useParams, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import PrivateMarketsProductTitle from '../components/PrivateMarketsProductTitle';
import AboutCard from '../components/AboutCard';
import PriceHistoryCard from '../components/PriceHistoryCard';
import FundingRoundsCard from '../components/FundingRoundsCard';
import MutualFundMarksCard from '../components/MutualFundMarksCard';
import ResearchReportCard from '../components/ResearchReportCard';
import NewsCard from '../components/NewsCard';
import { FundingRoundsData } from '../types/funding';
import { MutualFundsData, MutualFundData, MutualFundTableData, MutualFundMoreData } from '../types/mutualFund';
import { ResearchList } from '../types/research';
import { NewsList, NewsSummary } from '../types/news';

import {
  CompanyDetailInfo,
  NavOption,
  PriceHistoryData,
  PriceHistoryIndicator,
  PriceHistoryChartData
} from '../types/company';

export default function CompanyDetail() {
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const [selectedNavValue, setSelectedNavValue] = useState('about');
  const [companyDetailInfo, setCompanyDetailInfo] = useState<CompanyDetailInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [priceHistoryData, setPriceHistoryData] = useState<PriceHistoryData | null>(null);
  const [fundingRoundsData, setFundingRoundsData] = useState<FundingRoundsData>([]);
  const [mutualFundsData, setMutualFundsData] = useState<MutualFundsData>([]);
  const [mutualFundTableData, setMutualFundTableData] = useState<MutualFundTableData>([]);
  const [researchList, setResearchList] = useState<ResearchList>([]);
  const [newsList, setNewsList] = useState<NewsList>([]);
  const [newsListLoading, setNewsListLoading] = useState<boolean>(false);
  const name = searchParams.get('name');

  const navOptions: NavOption[] = [
    { label:'About', value:'about' },
    { label:'Price History', value:'price-history' },
    { label:'Funding Rounds', value:'funding-rounds' },
    { label:'Estimated Financials', value:'estimated-financials' },
    { label:'Mutual Fund Marks', value:'mutual-fund-marks' },
    { label:'Reporting Period', value:'research-reports' },
    { label:'News', value:'news' },
  ];

  const [priceHistoryIndicator, setPriceHistoryIndicator] = useState<PriceHistoryIndicator>({
    price: null,
    val: null,
    date: []
  });

  const [priceHistoryChartData, setPriceHistoryChartData] = useState<PriceHistoryChartData>({
    dates: [],
    names: [],
    datas: {}
  });

  const [orgPriceHistoryChartData, setOrgPriceHistoryChartData] = useState<PriceHistoryChartData>({
    dates: [],
    names: [],
    datas: {}
  });

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/polaris-api/market/company-info/${companyId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch company information');
        }
        const data = await response.json();
        setCompanyDetailInfo(data);
      } catch (err) {
        console.error('Error fetching company info:', err);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchCompanyInfo();
    }
  }, [companyId]);

  useEffect(() => {
    const fetchPriceHistory = async () => {
      if (!companyId) return;

      try {
        const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/polaris-api/market/company/${companyId}/price-series`);
        if (!response.ok) {
          throw new Error('Failed to fetch price history');
        }
        const data = await response.json();
        setPriceHistoryData(data);

        // Process price history data
        if (data && data.dates.length > 0) {
          const startDate = data.dates[0];
          const endDate = data.dates[data.dates.length - 1];

          // Update price history indicator
          setPriceHistoryIndicator({
            price: data.estValuations[endDate],
            val: data.estValuations[endDate],
            date: [startDate, endDate]
          });

          // Update chart data
          const newChartData: PriceHistoryChartData = {
            dates: data.dates,
            names: ['Augment Price', 'Funding Rounds', 'Fund Marks'],
            datas: {
              ...data,
              fundingRounds: data.fundingRounds
            }
          };

          setPriceHistoryChartData(newChartData);
          setOrgPriceHistoryChartData(JSON.parse(JSON.stringify(newChartData))); // Deep copy
        } else {
          // Reset all data if no dates
          setPriceHistoryIndicator({
            price: null,
            val: null,
            date: []
          });
          setPriceHistoryChartData({
            dates: [],
            names: [],
            datas: {}
          });
          setOrgPriceHistoryChartData({
            dates: [],
            names: [],
            datas: {}
          });
        }
      } catch (err) {
        console.error('Error fetching price history:', err);
        // Reset all data on error
        setPriceHistoryIndicator({
          price: null,
          val: null,
          date: []
        });
        setPriceHistoryChartData({
          dates: [],
          names: [],
          datas: {}
        });
        setOrgPriceHistoryChartData({
          dates: [],
          names: [],
          datas: {}
        });
      } finally {
        console.log(priceHistoryData)
        console.log(orgPriceHistoryChartData)
      }
    };

    fetchPriceHistory();
  }, [companyId]);

  useEffect(() => {
    const fetchFundingRounds = async () => {
      if (!companyId) return;

      try {
        const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/polaris-api/market/company/${companyId}/funding-rounds`);
        if (!response.ok) {
          throw new Error('Failed to fetch funding rounds data');
        }
        const data = await response.json();
        setFundingRoundsData(data);
        console.log('fundingRoundsData:', fundingRoundsData)
      } catch (err) {
        console.error('Error fetching funding rounds:', err);
        setFundingRoundsData([]);
      }
    };

    fetchFundingRounds();
  }, [companyId]);

  useEffect(() => {
    const fetchMutualFunds = async () => {
      if (!companyId) return;

      try {
        const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/polaris-api/market/company/${companyId}/mutual-fund-list`);
        if (!response.ok) {
          throw new Error('Failed to fetch mutual funds data');
        }
        const data = await response.json();
        setMutualFundsData(data);
        console.log('mutualFundsData:', mutualFundsData);

        if (data.length > 0) {
          const mutualFundList: MutualFundTableData = [];

          data.forEach((item: MutualFundData) => {
            const itemMoreList = item.mutualFundMoreDataList || [];
            const baseItem = {
              id: item.id,
              reportDate: item.reportDate,
              companyId: item.companyId,
              price: item.price,
              itemRowSpan: 1,
              itemColSpan: 1,
              mutualFundMoreDataList: item.mutualFundMoreDataList
            };

            itemMoreList.forEach((itemMore: MutualFundMoreData, index: number) => {
              const newItem = { ...baseItem };

              if (index === 0) {
                newItem.itemRowSpan = itemMoreList.length;
                newItem.itemColSpan = 1;
              } else {
                newItem.itemRowSpan = 0;
                newItem.itemColSpan = 0;
              }

              const transformedItem = {
                ...newItem,
                itemId: itemMore.id,
                dataPointId: itemMore.dataPointId,
                mutualFundId: itemMore.mutualFundId,
                ticker: itemMore.ticker,
                fundName: itemMore.fundName,
                shareClass: itemMore.shareClass,
                shareDescription: itemMore.shareDescription,
                secLink: itemMore.secLink,
                shares: itemMore.shares
              };

              mutualFundList.push(transformedItem);
            });
          });

          setMutualFundTableData(mutualFundList);
          console.log('mutualFundTableData:', mutualFundTableData);
        }
      } catch (err) {
        console.error('Error fetching mutual funds:', err);
        setMutualFundsData([]);
        setMutualFundTableData([]);
      }
    };

    fetchMutualFunds();
  }, [companyId]);

  useEffect(() => {
    const fetchResearchList = async () => {
      if (!companyId) return;

      try {
        const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/polaris-api/market/company/${companyId}/research-list`);
        if (!response.ok) {
          throw new Error('Failed to fetch research list');
        }
        const data = await response.json();
        setResearchList(data);
      } catch (err) {
        console.error('Error fetching research list:', err);
        setResearchList([]);
      }
    };

    fetchResearchList();
  }, [companyId]);

  useEffect(() => {
    const fetchNewsList = async () => {
      if (!companyId) return;

      try {
        setNewsListLoading(true)
        const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/polaris-api/market/company/${companyId}/news-list`);
        if (!response.ok) {
          throw new Error('Failed to fetch news list');
        }
        const data: NewsSummary[] = await response.json();

        // Transform the data by concatenating all newsArticlesList
        const transformedNewsList: NewsList = data.reduce((acc: NewsList, summary: NewsSummary) => {
          return [...acc, ...summary.newsArticlesList];
        }, []);

        setNewsList(transformedNewsList);
        console.log('newsList:', transformedNewsList)
        setNewsListLoading(false)
      } catch (err) {
        console.error('Error fetching news list:', err);
        setNewsList([]);
        setNewsListLoading(false)
      }
    };

    fetchNewsList();
  }, [companyId]);

  const handleNavChange = (value: string) => {
    setSelectedNavValue(value);
    const targetId = value;
    if (!targetId) return;

    const targetElement = document.getElementById(targetId)
    if (targetElement) {
      // 高级版：带顶部偏移（适用于固定头部）
      const headerHeight = document.getElementById('header')?.offsetHeight || 0;
      const productHeaderHeight = document.getElementById('productHeader')?.offsetHeight || 0;
      window.scrollTo({
        top: targetElement.offsetTop - headerHeight - productHeaderHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="px-4 sm:px-12 md:px-12 mb-20">
      <div className='hidden'>
        <p>Company ID: {companyId}</p>
        <p>Company Name: {name}</p>
      </div>

      <PrivateMarketsProductTitle
        id="productHeader"
        selectedNav={selectedNavValue}
        companyDetailInfo={companyDetailInfo}
        navOptions={navOptions}
        onChangeNav={handleNavChange}
        loading={loading}
      />

      <div className="page-content mx-w-[980px]">
        <div className="content">
          <div className="main-content">
            <div id="about">
              {companyDetailInfo && (
                <AboutCard companyDetailInfo={companyDetailInfo} />
              )}
            </div>
            <div id="price-history" className="hidden">
              {priceHistoryChartData && priceHistoryIndicator && (
                <PriceHistoryCard
                  priceHistoryChartData={priceHistoryChartData}
                  priceHistoryIndicator={priceHistoryIndicator}
                  onDateRangeChange={(value) => {
                    // TODO: Implement date range change logic
                    console.log('Date range changed:', value);
                  }}
                />
              )}
            </div>
            <div id="funding-rounds">
              <FundingRoundsCard data={fundingRoundsData} />
            </div>
            <div id="mutual-fund-marks">
              <MutualFundMarksCard tableData={mutualFundTableData} />
            </div>
            <div id="research-reports">
              <ResearchReportCard reportList={researchList} />
            </div>
            <div id="news">
              <NewsCard newsListLoading={newsListLoading} newsList={newsList} />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
